import { useEffect, useState } from "react";

import {
  api,
  apiErrorDetail,
  type AdminJiraConnection,
} from "../../../lib/api";

/** One list of project keys under a warning heading; renders nothing when empty. */
function Drift({ title, keys }: { title: string; keys: string[] | undefined }) {
  if (!keys || keys.length === 0) return null;
  return (
    <p className="mt-2 text-sm text-amber-800">
      <strong>{title}:</strong>{" "}
      <span className="font-mono">{keys.join(", ")}</span>
    </p>
  );
}

/**
 * Door-1 check: which account the proxy authenticates as, and where the service
 * account's Jira access and the allowlist disagree. Runs on mount; advisory only.
 */
export function JiraConnectionPanel() {
  const [result, setResult] = useState<AdminJiraConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Run the check, clearing the last result so a failure never sits beside it. */
  async function check() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.admin.jira.connection());
    } catch (e) {
      setError(apiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void check();
  }, []);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Connection check</h2>
          <p className="text-xs text-slate-500">
            Validates the token and compares what the service account can browse
            with the allowlist.
          </p>
        </div>
        <button
          type="button"
          onClick={check}
          disabled={busy}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          {busy ? "Checking…" : "Check again"}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      {result && !result.configured && (
        <p className="mt-2 text-sm text-slate-600">
          Jira is not configured on this deployment: JIRA_BASE_URL, JIRA_EMAIL
          or JIRA_API_TOKEN is unset, or JIRA_BASE_URL was rejected at startup
          (see the jira_base_url_rejected log entry).
        </p>
      )}
      {result?.configured && !result.ok && (
        <p className="mt-2 text-sm text-rose-700">
          {result.error === "auth_failed"
            ? "The token was rejected — it expired, was revoked, or lacks a scope. Rotate it (CLAUDE.md → Jira proxy)."
            : (result.detail ??
              `Jira did not answer (${result.error ?? "unknown error"}).`)}
        </p>
      )}
      {result?.ok && (
        <div className="mt-2 text-sm">
          <p className="text-emerald-700">
            Connected as {result.account?.display_name ?? "?"} (
            {result.account?.account_type ?? "?"}).
          </p>
          {result.complete === false && (
            <p className="mt-2 font-medium text-rose-700">
              The service account can see more projects than this check can
              list, so the lists below are incomplete. Its access is far broader
              than the allowlist: remove its group memberships in Jira.
            </p>
          )}
          <Drift
            title="Browsable but NOT allowlisted — fine if open to every Jira user; remove access to anything restricted"
            keys={result.visible_not_allowlisted}
          />
          <Drift
            title="Allowlisted but NOT browsable — queries return nothing until Browse is granted"
            keys={result.allowlisted_not_visible}
          />
          <Drift
            title="Renamed in Jira — still resolves; update the allowlist entry"
            keys={Object.entries(result.allowlisted_renamed ?? {}).map(
              ([from, to]) => `${from} → ${to}`,
            )}
          />
        </div>
      )}
    </div>
  );
}
